import { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import {
  FilteredNotesResult,
  GroupConfig,
  SortConfig,
  FilterConfig,
  Level,
  GroupByOption,
} from '../types'
import { sortNotesByDate, extractTasksFromNote } from '../utils'
import { isCalendarNote } from '../../../utils/note'
import GroupComponent from './GroupComponent'

function LoadingSpinner({ text = 'Loading...' }: { text?: string }) {
  return (
    <div className='w-full p-4 text-center'>
      <div className='loading-spinner inline-block h-5 w-5'></div>
      <p className='mt-2 text-base text-gray-600 dark:text-gray-400'>{text}</p>
    </div>
  )
}

type ItemListProps = {
  filteredNotesResult: FilteredNotesResult
  sortConfig: SortConfig
  groupConfig: GroupConfig
  filterConfig: FilterConfig
  onLoadMoreTasks?: () => void
  currentTaskLimit?: number
}

const ITEMS_PER_PAGE = 10
const SCROLL_THRESHOLD = '100px'

export default function ItemList({
  filteredNotesResult,
  sortConfig,
  groupConfig,
  filterConfig,
  onLoadMoreTasks,
  currentTaskLimit,
}: ItemListProps) {
  const { filteredNotes, taskGroups, taskLimitReached, totalTaskCount, isLoading, error } = filteredNotesResult

  // Lazy loading state
  const [displayedItemsCount, setDisplayedItemsCount] = useState(ITEMS_PER_PAGE)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const containerReference = useRef<HTMLDivElement>(null)
  const observerReference = useRef<IntersectionObserver | null>(null)

  // Sort notes using smart date-based sorting (memoized for performance)
  // Skip pre-sorting when GroupComponent will handle date grouping and sorting
  const sortedNotes = useMemo(() => {
    if (isLoading || error) return []

    // Check if we're going to group by date - if so, let GroupComponent handle sorting
    const willGroupByDate =
      groupConfig.option === GroupByOption.DATE ||
      (filterConfig.level === Level.TASKS &&
        filterConfig.noteType?.some((type) => isCalendarNote(type)))

    if (willGroupByDate) {
      // Don't pre-sort - let GroupComponent handle the sorting
      return [...filteredNotes]
    }

    // Use the intelligent date sorting algorithm for other cases
    return sortNotesByDate([...filteredNotes], sortConfig.direction)
  }, [
    filteredNotes,
    isLoading,
    error,
    sortConfig,
    groupConfig.option,
    filterConfig.level,
    filterConfig.noteType,
  ])

  // Calculate total count of items (tasks or notes) for display
  const totalCount = useMemo(() => {
    if (isLoading || error) return 0

    return filterConfig.level === Level.TASKS
      ? (() => {
          // Count all tasks across all filtered notes
          let total = 0
          for (const note of sortedNotes) {
            const tasks = extractTasksFromNote(note, filterConfig)
            total += tasks.length
          }
          return total
        })()
      : sortedNotes.length
  }, [sortedNotes, filterConfig, isLoading, error])

  // Reset displayed items when filters change
  useEffect(() => {
    setDisplayedItemsCount(ITEMS_PER_PAGE)
  }, [filteredNotes])

  // Load more items
  const loadMoreItems = useCallback(() => {
    if (isLoadingMore || displayedItemsCount >= sortedNotes.length) return

    setIsLoadingMore(true)
    setDisplayedItemsCount((previous) =>
      Math.min(previous + ITEMS_PER_PAGE, sortedNotes.length)
    )
    setIsLoadingMore(false)
  }, [isLoadingMore, displayedItemsCount, sortedNotes.length])

  // Set up intersection observer for infinite scrolling
  useEffect(() => {
    if (!containerReference.current) return
    const currentContainer = containerReference.current

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0]
        if (target.isIntersecting && !isLoadingMore) {
          loadMoreItems()
        }
      },
      {
        threshold: 0.1,
        rootMargin: SCROLL_THRESHOLD, // Start loading when SCROLL_THRESHOLD pixels away
      }
    )

    // Create a sentinel element
    const sentinel = document.createElement('div')
    sentinel.style.height = '1px'
    currentContainer.append(sentinel)
    observer.observe(sentinel)

    observerReference.current = observer

    return () => {
      observer.disconnect()
      if (sentinel.parentNode) {
        sentinel.remove()
      }
    }
  }, [loadMoreItems, isLoadingMore])

  // Loading state
  if (isLoading) {
    return <LoadingSpinner />
  }

  // Error state
  if (error) {
    return (
      <div className='p-4 text-center text-red-600 dark:text-red-400'>
        <p>Error loading notes: {error.message}</p>
      </div>
    )
  }

  // Empty state - check both notes and task groups
  const isEmpty =
    filterConfig.level === Level.TASKS
      ? !taskGroups || taskGroups.length === 0
      : filteredNotes.length === 0

  if (isEmpty) {
    return (
      <div className='p-4 text-center text-gray-600 dark:text-gray-400'>
        <p>
          No {filterConfig.level === Level.TASKS ? 'tasks' : 'notes'} match the
          current filters.
        </p>
      </div>
    )
  }

  // Use GroupComponent to handle different grouping strategies
  return (
    <div className='w-full'>
      {/* Count display */}
      <div className='mb-2 mt-2 px-2 md:px-4'>
        <div className='text-sm text-gray-600 dark:text-gray-400'>
          Count: {totalCount}
        </div>
      </div>

      <div className='w-full'>
        <GroupComponent
          notes={sortedNotes}
          filterConfig={filterConfig}
          groupBy={groupConfig.option}
          sortConfig={sortConfig}
          taskLimitReached={taskLimitReached}
          totalTaskCount={totalTaskCount}
          taskLimit={currentTaskLimit}
          onLoadMoreTasks={onLoadMoreTasks}
        />
      </div>
    </div>
  )
}
